/**
 * Article content — builds the table of contents from the headings of the
 * article body and highlights the one currently being read.
 *
 * Sharing is handled by the theme's own share-button snippet and share.js.
 *
 * The article body is a single rich text field, so the headings only exist in
 * the rendered HTML; they get an id here when they have none, and the list is
 * built from them at runtime.
 */
class ArticleContent extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-article-body]');

    this.measureOffset();
    this.buildTableOfContents();
  }

  disconnectedCallback() {
    if (!this.onScroll) return;
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
  }

  /**
   * How far below the top of the viewport a heading should come to rest: clear
   * of the sticky header — which reappears when scrolling up — plus a bit of
   * breathing room. The header is measured rather than assumed, as its height
   * differs between mobile and desktop.
   */
  measureOffset() {
    const header = document.querySelector('sticky-header, .header-wrapper, .section-header');
    const height = header ? header.getBoundingClientRect().height : 0;

    this.offset = Math.round(height) + ArticleContent.HEADING_GAP;
    this.style.setProperty('--article-content-scroll-offset', `${this.offset}px`);
  }

  buildTableOfContents() {
    const nav = this.querySelector('[data-toc]');
    const list = this.querySelector('[data-toc-list]');
    if (!nav || !list || !this.body) return;

    const levels = (this.dataset.headingLevels || 'h2').trim();
    const headings = Array.from(this.body.querySelectorAll(levels)).filter(
      (heading) => heading.textContent.trim() !== ''
    );

    if (headings.length === 0) return;

    const usedIds = new Set();

    headings.forEach((heading, index) => {
      if (!heading.id || document.querySelectorAll(`#${CSS.escape(heading.id)}`).length > 1) {
        heading.id = ArticleContent.uniqueId(heading.textContent, index, usedIds);
      }
      usedIds.add(heading.id);

      const item = document.createElement('li');
      item.className = `article-content__toc-item article-content__toc-item--${heading.tagName.toLowerCase()}`;

      const link = document.createElement('a');
      link.className = 'article-content__toc-link';
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();

      item.appendChild(link);
      list.appendChild(item);
    });

    nav.hidden = false;

    const links = Array.from(list.querySelectorAll('.article-content__toc-link'));

    this.setupSmoothScroll(links, headings);
    this.watchHeadings(headings, links);
  }

  /**
   * Scrolls to the heading rather than letting the browser jump to the anchor,
   * so that the move is animated and lands below the sticky header.
   */
  setupSmoothScroll(links, headings) {
    links.forEach((link, index) => {
      link.addEventListener('click', (event) => {
        const heading = headings[index];
        if (!heading) return;

        event.preventDefault();
        this.measureOffset();

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const top = heading.getBoundingClientRect().top + window.scrollY - this.offset;

        window.scrollTo({ top: Math.max(top, 0), behavior: reduced ? 'auto' : 'smooth' });

        // Keeps the address bar in step without the jump a hash change causes.
        history.replaceState(null, '', `#${heading.id}`);
      });
    });
  }

  /**
   * Marks the heading currently being read: the last one whose top has passed
   * the reading line, which sits just under the sticky header.
   */
  watchHeadings(headings, links) {
    let queued = false;

    const setActive = () => {
      queued = false;

      // A heading counts as reached once it passes where a jump would park it.
      const line = this.offset + 1;
      let index = 0;

      headings.forEach((heading, position) => {
        if (heading.getBoundingClientRect().top - line <= 0) index = position;
      });

      // Past the end of the article the last heading stays the current one,
      // and above the first heading the first one is highlighted already.
      links.forEach((link, position) => {
        link.classList.toggle('article-content__toc-link--active', position === index);
      });

      const active = links[index];
      if (!active) return;

      // Keep the highlighted entry in view when the list is long enough to
      // have its own scrollbar. Measured from the rendered boxes, as the list
      // is not the offset parent of its items.
      const item = active.parentElement;
      const list = item.parentElement;
      if (list.scrollHeight <= list.clientHeight) return;

      const itemBox = item.getBoundingClientRect();
      const listBox = list.getBoundingClientRect();

      if (itemBox.top < listBox.top) {
        list.scrollTop -= listBox.top - itemBox.top;
      } else if (itemBox.bottom > listBox.bottom) {
        list.scrollTop += itemBox.bottom - listBox.bottom;
      }
    };

    this.onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(setActive);
    };

    this.onResize = () => {
      this.measureOffset();
      this.onScroll();
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    setActive();
  }

  static uniqueId(text, index, usedIds) {
    const base =
      text
        .trim()
        .toLowerCase()
        .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60) || `section-${index + 1}`;

    let id = base;
    let suffix = 2;

    while (usedIds.has(id) || document.getElementById(id)) {
      id = `${base}-${suffix}`;
      suffix += 1;
    }

    return id;
  }
}

/* Space kept between the sticky header and a heading jumped to. */
ArticleContent.HEADING_GAP = 24;

if (!customElements.get('article-content')) {
  customElements.define('article-content', ArticleContent);
}
